import { z } from 'zod';
import type { DiagramType } from './registries';
import type React from 'react';
import type { PersonLabelGeometryContext } from './personLabelBounds';

export type ArrowheadType = 'none' | 'triangle' | 'circle' | 'circleOutline' | 'diamond' | 'bar' | 'outlineBlock' | 'backslash' | 'chevron';

export const connectorCommonProps = z.object({
  color: z.string().default('#333'),
  width: z.number().default(2),
  dashed: z.boolean().default(false),
  dash: z.array(z.number()).optional(),
  startArrow: z.object({ type: z.enum(['none','triangle','circle','circleOutline','diamond','bar','outlineBlock','backslash','chevron']).default('none'), size: z.number().default(6) }).default({ type: 'none', size: 6 }),
  endArrow: z.object({ type: z.enum(['none','triangle','circle','circleOutline','diamond','bar','outlineBlock','backslash','chevron']).default('triangle'), size: z.number().default(8) }).default({ type: 'triangle', size: 8 }),
});

export type ConnectorCommonProps = z.infer<typeof connectorCommonProps>;

export interface ConnectorSVGArgs<TProps> {
  points: number[];
  props: TProps;
}

export interface ConnectorSVGResult {
  svg: string;
  labelHandled?: boolean;
  label?: string;
}

export interface ConnectorKonvaArgs<TProps> {
  edge: { id: string; type?: string; from?: string; to?: string; attrs?: Record<string, unknown> };
  fromNode: { id: string } | undefined;
  toNode: { id: string } | undefined;
  props: TProps;
  // Interaction/selection context forwarded from canvas
  selected?: boolean;
  getShapeState?: <T extends Record<string, unknown>>(shapeId: string, baseState: T) => T;
  awareness?: unknown;
  registerRef?: (edgeId: string, node: unknown | null) => void;
  version?: number;
  labelGeometry?: PersonLabelGeometryContext;
  handlers: Record<string, unknown>;
}

export interface ConnectorManifest<TProps> {
  id: string;
  version: string;
  category: string;
  routingPolicyId?: string;
  label?: string;
  iconId?: string; // id of CSS mini icon to render in pickers/toolbars
  propsSchema: z.ZodType<TProps>;
  defaultProps: () => TProps;
  // Optional UI metadata for properties panel (applies to all connectors)
  ui?: {
    group?: 'identity' | 'style' | 'routing' | 'labels' | 'extras';
    collapsedByDefault?: boolean;
  };
  svg?: (args: ConnectorSVGArgs<TProps>) => string | ConnectorSVGResult;
  konvaNative?: (args: ConnectorKonvaArgs<TProps>) => React.ReactNode;
}

export interface ConnectorPack {
  meta: { id: string; name: string; version: string };
  diagramType: DiagramType;
  connectors: Array<ConnectorManifest<unknown>>;
}
